"use client";

import Link from "next/link";
import { useRef, useState } from "react";
import Image from "next/image";

import LanguageSwitch from "@/components/molecules/LanguageSwitch/LanguageSwitch";
import { useAuth } from "@/components/contexts/AuthProvider";
import ToggleMode from "@/components/atoms/ThemeSwitch/ThemeSwitch";
import useClickOutside from "@/hooks/useClickOutside";
import { useTranslations } from "next-intl";
import useWindowSize from "@/hooks/useWindowSize";
import { useTheme } from "next-themes";

import "./_navbar.scss";

const NavBar = ({ toggleMenu }: { toggleMenu: () => void }) => {
  const { user, isAuthenticated } = useAuth();
  const { width } = useWindowSize();
  const { theme, systemTheme } = useTheme();

  const l = useTranslations("leagues");
  const t = useTranslations("trends");
  const tMemberships = useTranslations("memberships");
  const tAccount = useTranslations("account");

  const mobileNavBarRef = useRef<HTMLDivElement>(null);
  const configContainerRef = useRef<HTMLLIElement>(null);
  const accountContainerRef = useRef<HTMLLIElement>(null);

  useClickOutside(mobileNavBarRef, () => toggleMenu());
  useClickOutside(configContainerRef, () => setShowConfigMenu(false));
  useClickOutside(accountContainerRef, () => setShowAccountMenu(false));

  const [showConfigMenu, setShowConfigMenu] = useState(false);
  const [showAccountMenu, setShowAccountMenu] = useState(false);

  const isDarkTheme =
    theme === "dark" ? true : theme === "light" ? false : systemTheme === "dark";

  return (
    <nav className="navbar navbar-expand-md">
      <div ref={mobileNavBarRef} className="navbar">
        <ul className="navbar-nav">
          <li onClick={toggleMenu}>
            <Link href="/trends" className="nav-link">
              {t("trends")}
            </Link>
          </li>
          <li onClick={toggleMenu}>
            <Link
              href="/bets/advises/pending"
              className="nav-link"
              style={{ position: "relative" }}
            >
              Bet Advisor
              <Image
                src="/icons/new.png"
                alt="New"
                width={30}
                height={30}
                loading="lazy"
                className="new-icon ms-3 brightness-animation"
              />
            </Link>
          </li>
          <li>
            <Link
              href="/league/0/results"
              className="nav-link"
              onClick={toggleMenu}
            >
              {l("leagueStats")}
            </Link>
          </li>
          <li>
            <Link
              href="/memberships"
              className="nav-link"
              onClick={toggleMenu}
            >
              {tMemberships("memberships")}
            </Link>
          </li>

          {isAuthenticated ? (
            width >= 768 ? (
              <li ref={accountContainerRef} className="position-relative">
                <div
                  className="nav-link pt-2"
                  onClick={() => setShowAccountMenu((prev) => !prev)}
                >
                  <Image
                    src={
                      isDarkTheme
                        ? "/icons/user-light.png"
                        : "/icons/user-dark.png"
                    }
                    alt={tAccount("loginRegister")}
                    width={30}
                    height={30}
                    loading="lazy"
                  />
                </div>

                {showAccountMenu && (
                  <ul
                    className="dropdown-menu show p-2"
                    style={{
                      position: "absolute",
                      backgroundColor: "var(--secondary-color)",
                      top: "100%",
                      right: 0,
                      zIndex: 1000,
                    }}
                  >
                    <li>
                      <Link
                        href="/account"
                        className="nav-link"
                        onClick={toggleMenu}
                      >
                        {tAccount("title")}
                      </Link>
                      <a
                        href="/api/auth/logout"
                        className="nav-link"
                        onClick={toggleMenu}
                      >
                        {tAccount("logout")}
                      </a>
                    </li>
                  </ul>
                )}
              </li>
            ) : (
              <li>
                <Link
                  href="/account"
                  className="nav-link"
                  onClick={toggleMenu}
                >
                  {tAccount("title")}
                </Link>
                <a
                  href="/api/auth/logout"
                  className="nav-link"
                  onClick={toggleMenu}
                >
                  {tAccount("logout")}
                </a>
              </li>
            )
          ) : (
            <li>
              <a
                href="/api/auth/login"
                className="nav-link"
                onClick={toggleMenu}
              >
                {tAccount("loginRegister")}
              </a>
            </li>
          )}

          {width >= 768 ? (
            <li ref={configContainerRef} className="position-relative">
              <div
                className="nav-link pt-2"
                onClick={() => setShowConfigMenu((prev) => !prev)}
              >
                <Image
                  src={
                    isDarkTheme
                      ? "/icons/config-light.png"
                      : "/icons/config-dark.png"
                  }
                  alt="config"
                  width={28}
                  height={28}
                  loading="lazy"
                />
              </div>

              {showConfigMenu && (
                <ul
                  className="dropdown-menu show p-2"
                  style={{
                    position: "absolute",
                    backgroundColor: "var(--secondary-color)",
                    top: "100%",
                    right: 0,
                    zIndex: 1000,
                  }}
                >
                  <li className="py-1">
                    <LanguageSwitch />
                  </li>
                  <li className="py-1">
                    <ToggleMode />
                  </li>
                </ul>
              )}
            </li>
          ) : (
            <>
              <li className={width >= 768 ? "my-auto" : "py-2"}>
                <LanguageSwitch />
              </li>
              <li onClick={toggleMenu}>
                <ToggleMode />
              </li>
            </>
          )}
        </ul>
      </div>
    </nav>
  );
};

export default NavBar;